import { DownloadOutlined } from '@ant-design/icons'
import { AutoComplete, Button, DatePicker, Divider, Form, Input, List, Modal, Spin } from 'antd'
import dayjs from 'dayjs'
import { FC, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useDownload } from '@hooks'

import { FlexCol, FlexRow } from '../../../components/StyledComponents'
import { useCreateRailcar } from '../../../hooks/railcar/useCreateRailcar'
import { BlocktrainDetail, RailcarDetail } from '../../../types'
import { formatNumberWithThousands } from '../../../utils'

interface RailcarModalProps {
  onCancel: () => void
  blockTrain: BlocktrainDetail
  railcar?: RailcarDetail
}

const RailcarModalViewAdmin: FC<RailcarModalProps> = ({ onCancel, blockTrain, railcar }) => {
  const { t } = useTranslation()
  const [form] = Form.useForm()
  const { downloadFile } = useDownload()

  const resetForm = (close?: boolean) => {
    form.resetFields()
    if (close) {
      onCancel()
    }
  }
  const { createRailcarIsFetching } = useCreateRailcar({ resetForm })

  const handleCancel = () => {
    resetForm(true)
  }

  const initialValues = useMemo(
    () => ({
      ...railcar,
      deliveryNoteDate: dayjs(railcar?.deliveryNoteDate),
      poNumber: railcar?.purchaseOrder?.PurchaseDocumentNumber,
      material: railcar?.material
        ? `${railcar.material?.nameShort} - ${railcar.material.sapCode.replace(/^(0+)/g, '')}`
        : '-',
      grossTn: formatNumberWithThousands(railcar?.grossTn ?? 0),
      tareTn: formatNumberWithThousands(railcar?.tareTn ?? 0),
      netTn: formatNumberWithThousands(railcar?.netTn ?? 0),
    }),
    [railcar],
  )

  useEffect(() => {
    form.setFieldsValue(initialValues)
  }, [railcar, form, initialValues])

  return (
    <Modal
      title={`${t('railcar')} #${blockTrain?.id}-${railcar?.railCarNumber}`}
      open={!!railcar}
      onCancel={handleCancel}
      cancelText={t('cancel')}
      className="entity-modal"
      width={900}
      footer={null}
    >
      <FlexRow>
        <FlexCol style={{ flex: 1 }}>
          <Form
            form={form}
            layout="inline"
            initialValues={initialValues}
            className="form-item-admin-view"
            style={{ width: '100%' }}
          >
            <Spin spinning={!!createRailcarIsFetching} style={{ width: '100%' }}>
              <Form.Item
                label={t('po_number')}
                name="poNumber"
                rules={[{ required: true, message: t('please_input_PO_number') }]}
              >
                <Input
                  className="border-unset"
                  value={railcar?.purchaseOrder?.PurchaseDocumentNumber || ''}
                  readOnly
                  placeholder={t('po_number')}
                />
              </Form.Item>

              <Form.Item
                label={t('material')}
                name="material"
                rules={[{ required: true, message: t('please_input_material') }]}
              >
                <Input
                  className="border-unset-material"
                  value={
                    railcar?.material
                      ? `${railcar.material?.nameShort} - ${railcar.material.sapCode.replace(
                          /^(0+)/g,
                          '',
                        )}`
                      : '-'
                  }
                  readOnly
                  placeholder={t('material')}
                />
              </Form.Item>

              <Form.Item
                label={t('railcar_ID')}
                name="railCarId"
                rules={[{ required: true, message: t('please_input_railcar_ID') }]}
              >
                <Input
                  className="border-unset"
                  placeholder={t('select_the_ID_of_the_railcar')}
                  readOnly
                />
              </Form.Item>

              <Form.Item
                label={t('delivery_note')}
                name="deliveryNote"
                rules={[
                  { required: true, message: t('please_input_delivery_note') },
                  { min: 5, message: t('minimum_5_characters') },
                  { max: 20, message: t('maximum_20_characters') },
                ]}
              >
                <AutoComplete
                  showSearch
                  placeholder={t('select_delivery_note')}
                  filterOption
                  onChange={value => form.setFieldsValue({ deliveryNote: value })}
                  onBlur={() => {
                    const currentValue = form.getFieldValue('deliveryNote')
                    if (currentValue && typeof currentValue === 'number') {
                      form.setFieldsValue({ deliveryNote: currentValue })
                    }
                  }}
                  options={[
                    ...new Set(blockTrain.railCars.map(({ deliveryNote }) => deliveryNote)),
                  ].map(value => ({ value }))}
                  disabled
                />
              </Form.Item>

              <Form.Item
                label={t('delivery_note_date')}
                name="deliveryNoteDate"
                rules={[{ required: true, message: t('please_input_delivery_note_date') }]}
                labelCol={{ span: t('delivery_note_date').length > 24 ? 24 : undefined }}
              >
                <DatePicker placeholder={t('select_date')} disabled />
              </Form.Item>

              <Form.Item
                label={t('gross_kg')}
                name="grossTn"
                rules={[{ required: true, message: t('please_input_gross_at_kg') }]}
              >
                <Input className="border-unset" placeholder={t('input_gross_kg')} readOnly />
              </Form.Item>

              <Form.Item
                label={t('tare_kg')}
                name="tareTn"
                rules={[{ required: true, message: t('please_input_tare_at_kg') }]}
              >
                <Input className="border-unset" placeholder={t('input_tare_kg')} readOnly />
              </Form.Item>

              <Form.Item
                label={t('net_kg')}
                name="netTn"
                rules={[{ required: true, message: t('please_input_net_at_kg') }]}
              >
                <Input className="border-unset" placeholder={t('input_net_kg')} readOnly />
              </Form.Item>
            </Spin>
          </Form>
        </FlexCol>
        <div>
          <Divider
            type="vertical"
            style={{ height: '100%', width: '1px', backgroundColor: 'grey', marginBlock: '1px' }}
          />
        </div>
        <FlexCol style={{ flex: 1 }}>
          <div style={{ height: '250px', overflowY: 'auto' }}>
            <List
              dataSource={railcar?.files}
              renderItem={item => (
                <List.Item
                  style={{ padding: '8px 16px' }}
                  actions={[
                    <Button
                      key="download"
                      icon={<DownloadOutlined />}
                      size="small"
                      onClick={() =>
                        downloadFile({ key: item.file.ticketFile, name: item.file.fileName })
                      }
                    />,
                  ]}
                >
                  <List.Item.Meta
                    title={<span style={{ fontSize: '14px' }}>{item.file.fileName}</span>}
                    description={
                      <span style={{ fontSize: '12px' }}>{t(item.type?.name || '')} </span>
                    }
                  />
                </List.Item>
              )}
            />
          </div>
        </FlexCol>
      </FlexRow>
    </Modal>
  )
}

export default RailcarModalViewAdmin
